import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { Suspense, lazy } from "react";
import { useSelector } from "react-redux";
import BrowserNotSupported from "./components/BrowserNotSupported";
import { shouldBlockBrowser } from "./utils/ieCompatibility";
import { PrivateRoutes } from "./routes/PrivateRoutes";
import { OpenRoutes } from "./routes/OpenRoutes";
import LoadingAnimation from "./components/common/LoadingAnimation.jsx";
import BlockedScreen from "./pages/BlockedScreen";

const RootLayout = lazy(() => import("./pages/RootLayout"));
const ErrorPage = lazy(() => import("./pages/ErrorPage"));
const Login = lazy(() => import("./pages/auth/Login"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/auth/VerifyEmail"));
const Profile = lazy(() => import("./pages/auth/Profile"));
const Product = lazy(() => import("./pages/Product/Product"));

const Shop = lazy(() => import("./pages/Shop.jsx"));
const Favorites = lazy(() => import("./pages/Favorites"));
const AffiliateMarketing = lazy(
  () => import("./pages/AffiliateMarketing/AffiliateMarketing"),
);
const Dashboard = lazy(() => import("./pages/AffiliateMarketing/Dashboard"));
const ImageUpload = lazy(
  () => import("./pages/AffiliateMarketing/ImageUpload"),
);
const UserImages = lazy(() => import("./pages/AffiliateMarketing/UserImages"));
const Transactions = lazy(
  () => import("./pages/AffiliateMarketing/Transactions"),
);
const SavedDesigns = lazy(() => import("./pages/SavedDesigns"));
const Prod = lazy(() => import("./pages/Product/createProduct/Cloth/Prod"));

const Coupons = lazy(() => import("./pages/Coupons"));
const Cart = lazy(() => import("./pages/Cart/Cart"));
const Checkout = lazy(() => import("./pages/Checkout/Checkout"));

const PreviewProduct = lazy(() => import("./pages/Product/PreviewProduct"));
const AffiliateProduct = lazy(
  () => import("./pages/AffiliateMarketing/AffiliateProduct"),
);
const LinkProduct = lazy(
  () => import("./pages/Product/linkProduct/LinkProduct"),
);
const OrderSuccess = lazy(() => import("./pages/Checkout/OrderSuccess"));
const OrderHistory = lazy(() => import("./pages/OrderHistory/OrderHistory"));
const Index = lazy(() => import("./pages/pages/home/Index.jsx"));
const AboutUs = lazy(() => import("./pages/pages/AboutUs.jsx"));
const TermsAndConditions = lazy(
  () => import("./pages/pages/TermsAndConditions.jsx"),
);
const PrivacyPolicy = lazy(() => import("./pages/pages/PrivacyPolicy.jsx"));
const FAQ = lazy(() => import("./pages/pages/FAQ.jsx"));
const ContactUs = lazy(() => import("./pages/pages/ContactUs.jsx"));
const ShippingReturns = lazy(() => import("./pages/pages/ShippingReturns.jsx"));
const CookiePolicy = lazy(() => import("./pages/pages/CookiePolicy.jsx"));
const Maintenance = lazy(() => import("./pages/Maintenance"));
const RateLimitExceededPage = lazy(
  () => import("./pages/pages/RateLimitExceededPage.js"),
);

const AffiliateAccessGuard = ({ children }) => {
  const { user } = useSelector((state) => state.auth);
  const isAffiliateBlocked =
    user?.isAffiliateBlocked &&
    (!user?.affiliateBlockedUntil ||
      new Date(user.affiliateBlockedUntil) > new Date());

  if (isAffiliateBlocked) {
    const blockInfo = {
      reason: user?.affiliateBlockReason,
      blockedUntil: user?.affiliateBlockedUntil,
    };
    return <BlockedScreen blockInfo={blockInfo} />;
  }

  return children;
};

const router = createBrowserRouter([
  {
    path: "/maintenance",
    element: <Maintenance />,
  },
  {
    path: "/rate-limit-exceeded",
    element: <RateLimitExceededPage />,
  },
  {
    path: "/",
    element: (
      <>
        <RootLayout />
      </>
    ),
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <Index /> },
      {
        path: "/login",
        element: (
          <OpenRoutes>
            <Login />
          </OpenRoutes>
        ),
      },
      {
        path: "/signup",
        element: (
          <OpenRoutes>
            <Signup />
          </OpenRoutes>
        ),
      },
      { path: "/verify-email", element: <VerifyEmail /> },
      { path: "/forgot-password", element: <ForgotPassword /> },
      { path: "reset-password/:token", element: <ResetPassword /> },
      { path: "/products", element: <Product /> },
      { path: "/shop", element: <Shop /> },
      {
        path: "/favorites",
        element: (
          <PrivateRoutes>
            <Favorites />
          </PrivateRoutes>
        ),
      },
      { path: "/image-upload", element: <ImageUpload /> },
      { path: "/products-details/:id", element: <Prod /> },
      { path: "/preview-product/:id", element: <PreviewProduct /> },
      {
        path: "/affiliate",
        element: (
          <PrivateRoutes>
            <AffiliateAccessGuard>
              <AffiliateMarketing />
            </AffiliateAccessGuard>
          </PrivateRoutes>
        ),
        children: [
          { index: true, element: <Dashboard /> },
          { path: "image-upload", element: <ImageUpload /> },
          { path: "user-images", element: <UserImages /> },
          { path: "affiliate-products", element: <AffiliateProduct /> },
          { path: "transactions", element: <Transactions /> },
        ],
      },
      {
        path: "/product-link/:random",
        element: (
          <PrivateRoutes>
            <LinkProduct />
          </PrivateRoutes>
        ),
      },
      {
        path: "/saved-designs",
        element: (
          <PrivateRoutes>
            <SavedDesigns />
          </PrivateRoutes>
        ),
      },
      { path: "/coupons", element: <Coupons /> },
      {
        path: "/cart",
        element: (
          <PrivateRoutes>
            <Cart />
          </PrivateRoutes>
        ),
      },
      { path: "/checkout", element: <Checkout /> },
      { path: "/order-success", element: <OrderSuccess /> },
      {
        path: "/order-history",
        element: (
          <PrivateRoutes>
            <OrderHistory />
          </PrivateRoutes>
        ),
      },
      { path: "/about-us", element: <AboutUs /> },
      { path: "/terms-and-conditions", element: <TermsAndConditions /> },
      { path: "/privacy-policy", element: <PrivacyPolicy /> },
      { path: "/faq", element: <FAQ /> },
      { path: "/contact-us", element: <ContactUs /> },
      { path: "/shipping-returns", element: <ShippingReturns /> },
      { path: "/cookie-policy", element: <CookiePolicy /> },
      {
        path: "/profile",
        element: (
          <PrivateRoutes>
            <Profile />
          </PrivateRoutes>
        ),
      },
    ],
  },
]);

function App() {
  if (shouldBlockBrowser()) {
    return <BrowserNotSupported />;
  }

  return (
    <Suspense
      fallback={
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#fdfcfa] dark:bg-gray-900 transition-opacity duration-500">
          <div className="text-center">
            <LoadingAnimation size="lg" className="mx-auto mb-6" />
            <div className="text-xl font-medium mt-4 bg-clip-text text-transparent bg-gradient-to-r from-teal-500 to-pink-500 animate-pulse-slow">
              OnPrintZ
            </div>
          </div>
        </div>
      }
    >
      <RouterProvider router={router} />
    </Suspense>
  );
}

export default App;
